/**
 * Financial for one lot: the entitled Deal Models with saved per-user results
 * (deals-v2), one model's saved result (exact decimals, read losslessly), the
 * personal GDV override and the saved property card fields Financial shows
 * beside a result (calculation price, auction result, system GDV).
 *
 * Every read here consumes saved results. Nothing polls or calculates; the
 * personal GDV save is synchronous on the server and refreshes saved views.
 */
import { useQuery } from '@tanstack/react-query'
import { isLosslessNumber } from 'lossless-json'
import { api, ApiError } from './http'
import type { PersonalSettings } from './types'
import type { DealModelDiagnostic, DealModelValueDescriptor } from './dealModelTypes'
import { withParsedLayouts, type DealModelLayout } from './dealModelLayout'
import { parseFloorAreaAssumption, type FloorAreaAssumption } from './floorAreaEvidence'
import { singleHomeValuationScope, type SingleHomeValuationScope } from './valuationPresentation'
export type { FloorAreaAssumption } from './floorAreaEvidence'

/* ---------- deals-v2 property models and saved results ---------- */

export interface PresentationOutput {
  outputId: string
  key: string
  label: string
  pointer: string
  semanticType: DealModelValueDescriptor['semanticType']
  unit: string | null
  nullable: boolean
  enumValues: string[]
}

export interface ModelPresentation { headlineOutputIds: string[]; outputs: PresentationOutput[] }

export interface DealsV2PropertyModelChoice {
  logicalModelId: string
  modelName: string
  ownership: 'builtIn' | 'private'
  presentation: ModelPresentation
  diagnostics: DealModelDiagnostic[]
  /** `updating` when the latest saved result is stale (see `freshness` and `staleReasons`). */
  availabilityState: 'current' | 'building' | 'unavailable' | 'updating'
  profileId: string | null
  /** Display layout (`deal-model-layout-v1`); absent on an older server or when its shape is not understood. */
  layout?: DealModelLayout
  /**
   * Whether the latest saved result is the current calculation, in the same
   * vocabulary as the result detail and the card; `null` when there is no
   * saved result. An older server sends none: it then follows availabilityState.
   */
  freshness: 'current' | 'updating' | null
  /** Why the latest saved result is stale (recipe, catalogue, plan, profile, selection, source, card, override); empty when current or not sent. */
  staleReasons: string[]
}

export interface DealsV2PropertyModels {
  contractVersion: 'deals-v2-property-models-v1'
  propertyId: string
  defaultLogicalModelId: string | null
  models: DealsV2PropertyModelChoice[]
}

export interface FinancialFloorArea { assumed: boolean; effectiveSqm: number | null; assumption: FloorAreaAssumption | null }

export interface FinancialValuationEvidence {
  /** Presence only: the notice distinguishes an explicit null (no subject GDV) from a value. */
  gdvPence: unknown
  gdvIndicative: boolean
  gdvReviewRequired: boolean
  warnings: string[]
}

export interface DealsV2ResultDetail {
  propertyId: string
  profileId: string
  resultId: string
  /** Exact saved input identity used when replacing a local draft after Apply. */
  overrideRevision?: string
  recipeSourceSha256?: string
  state: 'computed' | 'notComputable' | 'evaluationFailed'
  freshness: 'current' | 'updating'
  /** Why this saved result is not the latest calculation; empty when it is, or when an older server sends none. */
  staleReasons: string[]
  verdictBand: string | null
  output: unknown
  diagnostics: unknown
  calculatedAt: string
  effectiveAsOf: string
  /** Evidence from this result's exact current card; absent for retained history. */
  floorArea: FinancialFloorArea | null
  valuationEvidence: FinancialValuationEvidence | null
}

export function isFinancialSyncBusy(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 503 && error.code === 'FINANCIAL_SYNC_BUSY'
}

function contractError(message: string): never { throw new ApiError(502, 'deals_v2_contract_mismatch', message) }

/**
 * Stale reasons as sent, read loosely: an older server sends none (empty), a
 * non-array is ignored, non-string entries are dropped and each reason is kept
 * once. Unknown strings are kept so the mark can still say the result moved.
 */
export function parseStaleReasons(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return [...new Set(value.filter((reason): reason is string => typeof reason === 'string' && reason.length > 0))]
}

type ModelFreshness = { freshness: 'current' | 'updating' | null; staleReasons: string[] }

function modelFreshness(freshness: unknown, staleReasons: string[], availabilityState: unknown): ModelFreshness['freshness'] {
  if (staleReasons.length > 0 || freshness === 'updating') return 'updating'
  if (freshness === 'current') return 'current'
  if (freshness === null) return null
  // An older server sends no freshness: the availability state says the same.
  return availabilityState === 'current' ? 'current' : availabilityState === 'updating' ? 'updating' : null
}

/** Adds each model's parsed freshness and stale reasons; shared by both readers of the property-models cache entry. */
export function withModelFreshness<T>(models: T[]): Array<T & ModelFreshness> {
  return models.map(model => {
    if (!model || typeof model !== 'object') return model as T & ModelFreshness
    const row = model as T & { freshness?: unknown; staleReasons?: unknown; availabilityState?: unknown }
    const staleReasons = parseStaleReasons(row.staleReasons)
    return { ...model, freshness: modelFreshness(row.freshness, staleReasons, row.availabilityState), staleReasons }
  })
}

/** The property-models envelope for one property, checked against the request and with layouts and freshness parsed. */
export function parseDealsV2PropertyModels(envelope: unknown, propertyId: string): DealsV2PropertyModels {
  const models = (envelope as { data?: Omit<DealsV2PropertyModels, 'models'> & { models: unknown } } | null | undefined)?.data
  if (!models || models.propertyId !== propertyId || models.contractVersion !== 'deals-v2-property-models-v1' || !Array.isArray(models.models)) contractError('Financial model list did not match this property')
  return { ...models, models: withModelFreshness(withParsedLayouts(models.models as Array<Omit<DealsV2PropertyModelChoice, 'layout' | 'freshness' | 'staleReasons'> & { layout?: unknown }>)) }
}

type RawResultDetail = Omit<DealsV2ResultDetail, 'floorArea' | 'valuationEvidence' | 'staleReasons'> & { floorArea?: unknown; valuationEvidence?: unknown; staleReasons?: unknown }

/** One saved result, checked against the requested profile and property; evidence and stale reasons parsed. */
export function parseDealsV2ResultDetail(envelope: unknown, profileId: string, propertyId: string): DealsV2ResultDetail {
  const detail = (envelope as { data?: RawResultDetail } | null | undefined)?.data
  if (!detail || detail.propertyId !== propertyId || detail.profileId !== profileId || (detail.freshness !== 'current' && detail.freshness !== 'updating')) contractError('Financial result did not match this property and profile')
  return { ...detail, staleReasons: parseStaleReasons(detail.staleReasons), floorArea: parseFinancialFloorArea(detail.floorArea, detail.freshness), valuationEvidence: parseFinancialValuationEvidence(detail.valuationEvidence, detail.freshness) }
}

export function parseFinancialValuationEvidence(value: unknown, freshness: DealsV2ResultDetail['freshness']): FinancialValuationEvidence | null {
  if (value == null || freshness !== 'current') return null
  const row = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
  if (!row || typeof row.gdvReviewRequired !== 'boolean' || (row.gdvIndicative != null && typeof row.gdvIndicative !== 'boolean')
    || (row.gdvPence !== null && typeof row.gdvPence !== 'number' && !isLosslessNumber(row.gdvPence))
    || !Array.isArray(row.warnings) || !row.warnings.every(warning => typeof warning === 'string')) contractError('Financial valuation evidence is invalid')
  return { gdvPence: row.gdvPence, gdvIndicative: row.gdvIndicative === true, gdvReviewRequired: row.gdvReviewRequired, warnings: row.warnings as string[] }
}

export function parseFinancialFloorArea(value: unknown, freshness: DealsV2ResultDetail['freshness']): FinancialFloorArea | null {
  // Older APIs and retained results cannot establish current floor ownership.
  if (value == null || freshness !== 'current') return null
  const fail = (): never => contractError('Financial floor evidence is invalid')
  const row = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fail()
  const positive = (entry: unknown) => {
    const numeric = isLosslessNumber(entry) ? Number(entry.toString()) : entry
    return typeof numeric === 'number' && Number.isFinite(numeric) && numeric > 0 ? numeric : fail()
  }
  if (typeof row.assumed !== 'boolean') return fail()
  const effectiveSqm = row.effectiveSqm == null ? null : positive(row.effectiveSqm)
  if (!row.assumed) return { assumed: false, effectiveSqm, assumption: null }
  const assumption = parseFloorAreaAssumption(row.assumption, effectiveSqm, fail, value => isLosslessNumber(value) ? Number(value.toString()) : value)
  return { assumed: true, effectiveSqm, assumption }
}

export const financialKeys = {
  propertyModels: (propertyId: string) => ['dealsV2', 'propertyModels', propertyId] as const,
  result: (profileId: string, propertyId: string) => ['dealsV2', 'result', profileId, propertyId] as const,
  areaPotential: (propertyId: string) => ['areaPotential', propertyId, 'financial'] as const,
}

const financialRetryDelay = (attempt: number) => Math.min(500 * 2 ** attempt, 2_000)

export function useDealsV2PropertyModels(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.propertyModels(propertyId),
    // Shares its cache entry with dealModels.ts; both parse the optional layout and freshness the same way.
    queryFn: async ({ signal }) => parseDealsV2PropertyModels(await api<unknown>('dealsV2PropertyModels', { params: { propertyId }, signal }), propertyId),
    enabled: enabled && propertyId.length > 0,
    placeholderData: (previous) => previous?.propertyId === propertyId ? previous : undefined,
    // A busy mirror sync is retried briefly with backoff; nothing else is retried.
    retry: (failureCount, error) => failureCount < 3 && isFinancialSyncBusy(error),
    retryDelay: financialRetryDelay,
    refetchOnWindowFocus: false,
  })
}

export function useDealsV2ResultDetail(profileId: string, propertyId: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.result(profileId, propertyId),
    queryFn: async ({ signal }): Promise<DealsV2ResultDetail> => parseDealsV2ResultDetail(await api<unknown>('dealsV2ProfileResult', { params: { profileId, propertyId }, signal, lossless: true }), profileId, propertyId),
    enabled: enabled && profileId.length > 0 && propertyId.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/* ---------- bids ---------- */

export const DEAL_MODEL_BID_STATES = ['computed', 'noPassingBid', 'searchLimitReached', 'missingInputs', 'notApplicable', 'invalidBounds', 'monotonicityViolation', 'evaluationFailed', 'resourceLimit', 'unsupported'] as const
export type DealModelBidState = typeof DEAL_MODEL_BID_STATES[number]
export const BID_PROFILES = ['none', 'light', 'standard', 'full', 'custom'] as const
export type DealModelBidProfile = typeof BID_PROFILES[number]

export interface ProfileBid {
  state: DealModelBidState
  /** Exact non-negative integer pence, never a JavaScript number. */
  maxBidPence: string | null
  limitingConstraint: string | null
  passesAtMarketedPrice: boolean | null
  atMaximum: Record<string, unknown> | null
  proof: { diagnosticMessage?: string | null } & Record<string, unknown>
}

export interface BidEvaluation { profile: DealModelBidProfile; bid: ProfileBid }
export interface HomeBuyerBid { state: DealModelBidState; maxBidPence: string | null; passesAtMarketedPrice: boolean | null; limitingConstraint: string | null }

/* ---------- personal settings (GDV override): one implementation in personalSettings.ts ---------- */
export { mergePersonalSettings, parsePersonalSettings, usePatchPersonalGdv, usePersonalAccess, usePersonalSettings } from './personalSettings'
import { parsePersonalSettings } from './personalSettings'

/* ---------- the saved card fields Financial shows beside a result ---------- */

export interface FinancialAuctionResult { status: string; highestBidPence: number | null; salePricePence: number | null; reserveMet: boolean | null }

export interface FinancialCardView {
  valuationScope?: SingleHomeValuationScope | null
  personalSettings: PersonalSettings | null
  calculationState: 'ready' | 'failed' | 'pending'
  calculationFreshness: 'current' | 'updating' | null
  listing: {
    marketedPricePence: number | null
    calculationPricePence: number | null
    calculationPriceSource: string | null
    auctionResult: FinancialAuctionResult | null
  } | null
  systemGdvPence: number | null
}

function parseFinancialCardView(value: unknown, propertyId: string): FinancialCardView {
  const fail = (message: string): never => { throw new ApiError(502, 'area_potential_contract_mismatch', message) }
  const rec = (entry: unknown): Record<string, unknown> | null => entry && typeof entry === 'object' && !Array.isArray(entry) ? entry as Record<string, unknown> : null
  const row = rec(rec(value)?.data) ?? fail('Property card read must be an object')
  if (row.propertyId !== propertyId) fail('Property card read belongs to another property')
  const state = row.calculationState === 'ready' || row.calculationState === 'failed' || row.calculationState === 'pending' ? row.calculationState : fail('Unknown calculation state')
  const num = (entry: unknown) => typeof entry === 'number' && Number.isFinite(entry) ? entry : null
  const card = rec(row.card)
  const listing = rec(card?.listing)
  const auction = rec(listing?.auctionResult)
  const freshness = rec(row.calculation)?.freshness
  return {
    personalSettings: row.personalSettings == null ? null : parsePersonalSettings(row.personalSettings, propertyId),
    calculationState: state,
    calculationFreshness: freshness === 'current' || freshness === 'updating' ? freshness : null,
    listing: listing ? {
      marketedPricePence: num(listing.marketedPricePence),
      calculationPricePence: num(listing.calculationPricePence),
      calculationPriceSource: typeof listing.calculationPriceSource === 'string' ? listing.calculationPriceSource : null,
      auctionResult: auction && typeof auction.status === 'string' ? { status: auction.status, highestBidPence: num(auction.highestBidPence), salePricePence: num(auction.salePricePence), reserveMet: typeof auction.reserveMet === 'boolean' ? auction.reserveMet : null } : null,
    } : null,
    systemGdvPence: num(rec(card?.valuation)?.gdvPence),
    valuationScope: singleHomeValuationScope({ assetClass: typeof rec(card?.property)?.assetClass === 'string' ? rec(card?.property)?.assetClass as string : null }, rec(rec(row.diligence)?.inputQuality)),
  }
}

/** The saved property card (area-potential read) narrowed to the fields Financial needs. */
export function useFinancialCardView(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: financialKeys.areaPotential(propertyId),
    queryFn: async ({ signal }) => parseFinancialCardView(await api<unknown>('areaPotential', { params: { propertyId }, signal }), propertyId),
    enabled: enabled && propertyId.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
